/*
 * Copyright (c) 2023 Bolmate B.V.
 */
import './style/contextAccount.scss';

import React, {useContext, useMemo, useRef} from 'react';
import {UserRole} from '../../../util/interfaces';
import {setElementHighlight} from '../../../util/utils';
import {AppContext} from '../../misc/appContext';
import Account from '../../../models/accountModel';
import {DVRDGroupedSelect, GroupedSelectItem, GroupedSelectRef} from '@dvrd/dvr-controls';

interface Props {
    onChangeAccount: (id: string) => void;
}

export default function ContextAccount(props: Props) {
    const context = useContext(AppContext);
    const {accounts, account} = context.accountContext;
    const {onChangeAccount} = props;
    const [ownedAccounts, accessAccounts]: [Array<Account>, Array<Account>] = useMemo(() => {
        const owned: Array<Account> = [];
        const access: Array<Account> = [];
        for (const account of accounts) {
            if (account.userRole === UserRole.OWNER) owned.push(account);
            else access.push(account);
        }
        return [owned, access];
    }, [accounts]);
    const items: Array<GroupedSelectItem> = useMemo(() => {
        let items: Array<GroupedSelectItem> = [];
        if (accessAccounts.length) {
            items.push({
                label: 'Eigen accounts',
                children: ownedAccounts.map((account: Account) => ({
                    label: account.labelWithSellerID,
                    value: account.id
                })).concat({value: '', label: '+ Verkoopaccount'})
            });
            items.push({
                label: 'Andere accounts', children: accessAccounts.map((account: Account) => ({
                    label: account.labelWithSellerID,
                    value: account.id
                }))
            });
        } else items = ownedAccounts.map((account: Account) => ({
            label: account.labelWithSellerID,
            value: account.id
        })).concat({value: '', label: '+ Verkoopaccount'});
        return items;
    }, [ownedAccounts, accessAccounts]);
    const rootRef = useRef<HTMLDivElement>(null);
    const selectRef = useRef<GroupedSelectRef>(null);

    function onClickRoot(evt: React.MouseEvent) {
        evt.stopPropagation();
        setElementHighlight('div.account-select', false);
        selectRef.current?.toggle();
    }

    function renderAccountsContainer() {
        return (
            <DVRDGroupedSelect className='grouped-account-select' items={items} onChange={onChangeAccount}
                               value={account?.id ?? ''} ref={selectRef} maxItemsHeight='70dvh'/>
        );
    }

    return (
        <div className='context-account' onClick={onClickRoot} ref={rootRef}>
            <label className='select-label'>Verkoopaccount:</label>
            {renderAccountsContainer()}
            <p className='message-container'>
                Klik hier om een extra account toe te voegen.
            </p>
        </div>
    );
}